import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, InputNumber, Select, Switch, Button, Card, message, Spin, Space } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getChannelConfig,
  createChannelConfig,
  updateChannelConfig,
} from '@/api/notificationApi';
import AdminNav from './AdminNav';

const CHANNEL_TYPES = ['email', 'sms'];
const PROVIDERS: Record<string, string[]> = {
  email: ['smtp', 'sendgrid'],
  sms: ['sms_stub'],
};

export default function ChannelConfigForm() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [channelType, setChannelType] = useState<string>('email');

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getChannelConfig(id);
      setChannelType(data.channel_type);
      form.setFieldsValue({
        channel_type: data.channel_type,
        provider: data.provider,
        is_active: data.is_active,
        config_data: data.config_data,
        priority: data.priority,
        max_retries: data.max_retries,
        retry_delay_seconds: data.retry_delay_seconds,
      });
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [id, form, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (values: Record<string, unknown>) => {
    setSaving(true);
    try {
      if (isEdit) {
        await updateChannelConfig(id!, {
          is_active: values.is_active as boolean,
          config_data: values.config_data as string,
          priority: values.priority as number,
          max_retries: values.max_retries as number,
          retry_delay_seconds: values.retry_delay_seconds as number,
        });
      } else {
        await createChannelConfig({
          channel_type: values.channel_type as string,
          provider: values.provider as string,
          is_active: values.is_active as boolean,
          config_data: values.config_data as string,
          priority: values.priority as number,
          max_retries: values.max_retries as number,
          retry_delay_seconds: values.retry_delay_seconds as number,
        });
      }
      message.success(t('notification.config_saved'));
      navigate('/admin/channel-configs');
    } catch {
      message.error(t('app.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div>
      <AdminNav />
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/admin/channel-configs')}
        style={{ marginBottom: 16 }}
      >
        {t('app.back')}
      </Button>

      <Card title={isEdit ? t('notification.edit_config_title') : t('notification.create_config_title')}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_active: true,
            priority: 1,
            max_retries: 3,
            retry_delay_seconds: 60,
            channel_type: 'email',
            provider: 'smtp',
          }}
        >
          <Form.Item
            name="channel_type"
            label={t('notification.config_field.channel_type')}
            rules={[{ required: true }]}
          >
            <Select
              disabled={isEdit}
              options={CHANNEL_TYPES.map((ch) => ({
                value: ch,
                label: t(`notification.channel.${ch}`),
              }))}
              onChange={(v) => {
                setChannelType(v);
                form.setFieldValue('provider', PROVIDERS[v]?.[0]);
              }}
            />
          </Form.Item>

          <Form.Item
            name="provider"
            label={t('notification.config_field.provider')}
            rules={[{ required: true }]}
          >
            <Select
              disabled={isEdit}
              options={(PROVIDERS[channelType] || []).map((p) => ({
                value: p,
                label: t(`notification.provider.${p}`, p),
              }))}
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label={t('notification.config_field.is_active')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="config_data"
            label={t('notification.config_field.config_data')}
            rules={[{ required: true }]}
            extra={t('notification.config_data_hint')}
          >
            <Input.TextArea rows={6} placeholder='{"host":"smtp.example.com","port":587,...}' />
          </Form.Item>

          <Space>
            <Form.Item
              name="priority"
              label={t('notification.config_field.priority')}
            >
              <InputNumber min={1} max={99} />
            </Form.Item>

            <Form.Item
              name="max_retries"
              label={t('notification.config_field.max_retries')}
            >
              <InputNumber min={0} max={10} />
            </Form.Item>

            <Form.Item
              name="retry_delay_seconds"
              label={t('notification.config_field.retry_delay')}
            >
              <InputNumber min={0} max={3600} />
            </Form.Item>
          </Space>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>
              {t('app.save')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
